const STORAGE_KEY = "rabiitrip_guest_session";
const BOARD_GUEST_STORAGE_KEY = "rabiitrip_destination_board_guest_session";

const defaultGuestState = () => ({
  shiori: [],
  membersByShioriId: {},
  schedulesByShioriId: {},
  checklistsByShioriId: {},
  wishlistsByShioriId: {},
  warikansByShioriId: {},
  warikanMembersByShioriId: {},
  exchangeRatesByShioriId: {},
  shioriSeq: 1,
  memberSeq: 1,
  scheduleSeq: 1,
  checklistSeq: 1,
  wishlistSeq: 1,
  warikanSeq: 1,
});

const loadGuestState = () => {
  if (typeof localStorage === "undefined") return defaultGuestState();
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return defaultGuestState();
    const parsed = JSON.parse(saved);
    return {
      ...defaultGuestState(),
      ...parsed,
      shiori: Array.isArray(parsed.shiori) ? parsed.shiori : [],
      membersByShioriId: parsed.membersByShioriId || {},
      schedulesByShioriId: parsed.schedulesByShioriId || {},
      checklistsByShioriId: parsed.checklistsByShioriId || {},
      wishlistsByShioriId: parsed.wishlistsByShioriId || {},
      warikansByShioriId: parsed.warikansByShioriId || {},
      warikanMembersByShioriId: parsed.warikanMembersByShioriId || {},
      exchangeRatesByShioriId: parsed.exchangeRatesByShioriId || {},
    };
  } catch (error) {
    console.error("Failed to read guest session:", error);
    return defaultGuestState();
  }
};

const guestState = loadGuestState();

const saveGuestState = () => {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(guestState));
};

export const isGuestMode = () => {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  return path.startsWith("/trial") || !localStorage.getItem("token");
};

const readBoardGuestSessions = () => {
  if (typeof localStorage === "undefined") return {};
  try {
    const saved = localStorage.getItem(BOARD_GUEST_STORAGE_KEY);
    return saved ? JSON.parse(saved) || {} : {};
  } catch {
    console.error("Failed to read destination board guest session");
    return {};
  }
};

const writeBoardGuestSessions = (sessions) => {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(BOARD_GUEST_STORAGE_KEY, JSON.stringify(sessions || {}));
};

export const createGuestId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `guest-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const saveDestinationBoardGuestSession = (session = {}) => {
  const boardId =
    session.boardId ||
    session.board_id ||
    session.destinationBoardId ||
    session.BOARD_ID;
  if (!boardId) return null;
  const normalized = {
    boardId: String(boardId),
    guestSessionToken:
      session.guestSessionToken || session.guest_session_token || "",
    guestSessionExpiresAt:
      session.guestSessionExpiresAt || session.guest_session_expires_at || null,
    guestId: session.guestId || session.guest_id || session.GUEST_ID || createGuestId(),
    guestName: session.guestName || session.guest_name || session.GUEST_NAME || session.memberName || session.member_name || session.MEMBER_NAME || "ゲスト",
    memberCode:
      session.memberCode ||
      session.member_code ||
      session.MEMBER_CODE ||
      null,
  };
  const legacyShareToken =
    session.shareToken || session.share_token || session.SHARE_TOKEN || "";
  if (!normalized.guestSessionToken && legacyShareToken) {
    normalized.shareToken = legacyShareToken;
  }
  const sessions = readBoardGuestSessions();
  sessions[normalized.boardId] = normalized;
  writeBoardGuestSessions(sessions);
  return normalized;
};

export const getDestinationBoardGuestSession = (boardId) => {
  if (!boardId) return null;
  const sessions = readBoardGuestSessions();
  return sessions[String(boardId)] || null;
};

export const findDestinationBoardGuestSessionByShareToken = (shareToken) => {
  if (!shareToken) return null;
  const sessions = readBoardGuestSessions();
  return Object.values(sessions).find((session) => session?.shareToken === shareToken) || null;
};

export const getDestinationBoardGuestHeaders = (boardId) => {
  const session = getDestinationBoardGuestSession(boardId);
  if (session?.guestSessionToken) {
    const expiresAt = Date.parse(session.guestSessionExpiresAt || "");
    if (Number.isFinite(expiresAt) && expiresAt <= Date.now()) {
      return {};
    }
    return {
      "X-Destination-Board-Guest-Session": session.guestSessionToken,
    };
  }
  if (!session?.shareToken || !session?.guestId) {
    return {};
  }
  return {
    "X-Destination-Board-Share-Token": session.shareToken,
    "X-Rabiitrip-Guest-Id": session.guestId,
  };
};

const toDestinationJson = (destinations = []) =>
  JSON.stringify(Array.isArray(destinations) ? destinations : []);

export const fetchGuestShiori = () => guestState.shiori;

export const createGuestShiori = (data) => {
  const now = new Date().toISOString();
  const id = `guest-${guestState.shioriSeq++}`;
  const shiori = {
    SHIORI_NOV: id,
    SUBJECT_NMV: data.title,
    TRIP_FROM: data.startDate,
    TRIP_TO: data.endDate,
    BUDGET: data.budget ?? null,
    DESTINATION_TYPE: data.destinationType || "domestic",
    DESTINATIONS: toDestinationJson(data.destinations),
    ENTRY_USER: "guest",
    TOTAL_COST_JPY: 0,
    HAS_IMAGE: Boolean(data.imageUrl),
    IMAGE_URL: data.imageUrl || "",
    IMAGE_ALT: data.imageAlt || "",
    CREATED_AT: now,
    UPDATED_AT: now,
    __guest: true,
  };
  guestState.shiori.unshift(shiori);
  guestState.membersByShioriId[id] = [];
  guestState.schedulesByShioriId[id] = [];
  guestState.checklistsByShioriId[id] = [];
  guestState.wishlistsByShioriId[id] = [];
  guestState.warikansByShioriId[id] = [];
  guestState.warikanMembersByShioriId[id] = [];
  guestState.exchangeRatesByShioriId[id] = [];
  saveGuestState();
  return { ...shiori, shiori_id: id };
};

export const updateGuestShiori = (shioriId, data) => {
  const target = guestState.shiori.find((item) => item.SHIORI_NOV === shioriId);
  if (!target) throw new Error("しおりが見つかりません");

  Object.assign(target, {
    SUBJECT_NMV: data.title ?? target.SUBJECT_NMV,
    TRIP_FROM: data.startDate ?? target.TRIP_FROM,
    TRIP_TO: data.endDate ?? target.TRIP_TO,
    BUDGET: data.budget ?? target.BUDGET,
    DESTINATION_TYPE: data.destinationType ?? target.DESTINATION_TYPE,
    DESTINATIONS: data.destinations
      ? toDestinationJson(data.destinations)
      : target.DESTINATIONS,
    HAS_IMAGE:
      data.imageUrl !== undefined ? Boolean(data.imageUrl) : target.HAS_IMAGE,
    IMAGE_URL: data.imageUrl ?? target.IMAGE_URL,
    IMAGE_ALT: data.imageAlt ?? target.IMAGE_ALT,
    UPDATED_AT: new Date().toISOString(),
  });
  saveGuestState();
  return target;
};

export const deleteGuestShiori = (shioriId) => {
  guestState.shiori = guestState.shiori.filter(
    (item) => item.SHIORI_NOV !== shioriId,
  );
  delete guestState.membersByShioriId[shioriId];
  delete guestState.schedulesByShioriId[shioriId];
  delete guestState.checklistsByShioriId[shioriId];
  delete guestState.wishlistsByShioriId[shioriId];
  delete guestState.warikansByShioriId[shioriId];
  delete guestState.warikanMembersByShioriId[shioriId];
  delete guestState.exchangeRatesByShioriId[shioriId];
  saveGuestState();
  return { success: true };
};

export const fetchGuestMembers = (shioriId) =>
  guestState.membersByShioriId[shioriId] || [];

export const createGuestMember = (shioriId, data) => {
  const member = {
    MEMBER_CODE: `guest-member-${guestState.memberSeq++}`,
    MEMBER_NAME:
      data.member_name || data.memberName || data.name || "ゲスト",
    APP_USER_CODE: data.app_user_code || null,
    INVITE_EMAIL: data.invite_email || null,
  };
  guestState.membersByShioriId[shioriId] = [
    ...(guestState.membersByShioriId[shioriId] || []),
    member,
  ];
  saveGuestState();
  return member;
};

export const updateGuestMember = (shioriId, memberCode, data = {}) => {
  const members = guestState.membersByShioriId[shioriId] || [];
  const target = members.find((member) => member.MEMBER_CODE === memberCode);
  if (!target) throw new Error("メンバーが見つかりません");

  Object.assign(target, {
    MEMBER_NAME:
      data.member_name || data.memberName || data.name || target.MEMBER_NAME,
    APP_USER_CODE:
      data.app_user_code !== undefined
        ? data.app_user_code
        : target.APP_USER_CODE,
    INVITE_EMAIL:
      data.invite_email !== undefined ? data.invite_email : target.INVITE_EMAIL,
  });
  saveGuestState();
  return target;
};

export const deleteGuestMember = (shioriId, memberCode) => {
  guestState.membersByShioriId[shioriId] = (
    guestState.membersByShioriId[shioriId] || []
  ).filter((member) => member.MEMBER_CODE !== memberCode);
  saveGuestState();
  return { success: true };
};

export const fetchGuestSchedules = (shioriId) =>
  guestState.schedulesByShioriId[shioriId] || [];

const buildScheduleMembers = (shioriId, members = []) => {
  const shioriMembers = fetchGuestMembers(shioriId);
  return members
    .map((item) => {
      const member = shioriMembers.find(
        (candidate) => candidate.MEMBER_CODE === item.member_code,
      );
      if (!member) return null;
      return {
        ...member,
        SPLIT_PARTICIPATE_FLG: item.split_participate_flg || "1",
        PAID_AMOUNT: 0,
      };
    })
    .filter(Boolean);
};

const toGuestSchedule = (data, lineNon) => ({
  LINE_NON: lineNon,
  SHIORI_NOV: data.shioriId,
  SUBJECT_NMV: data.title,
  START_YMD: data.startDate,
  START_TIME: data.startTime,
  END_YMD: data.endDate || data.startDate,
  END_TIME: data.endTime || null,
  DISPLAY_START_TIME: data.startTime,
  TIME_DIFFERENCE_NUM: Number(data.timeDifference || 9),
  CATEGORY_CDV: data.category || null,
  URL_NMV: data.url || null,
  TRANSPORTATION_NMV: data.transportation || null,
  TEL_AREA_CODE: "",
  TEL_CITY_CODE: "",
  TEL_SUBSCRIBER_NUMBER: data.tel || "",
  ADDRESS_NMV: data.address || null,
  GOOGLE_PLACE_NMV: data.googlePlaceNmv || null,
  GOOGLE_PLACE_ID: data.googlePlaceId || null,
  LATITUDE: data.latitude ?? null,
  LONGITUDE: data.longitude ?? null,
  REMARKS: data.remarks || null,
  COST: data.cost ?? null,
  CURRENCY: data.currency || "JPY",
  RESERVATION_FLG: data.reservation ? "1" : "0",
  RESERVATION_DONE_FLG: data.reservationDone ? "1" : "0",
  RESERVATION_DOCUMENT_NAME: data.reservationDocumentName || null,
  RESERVATION_DOCUMENT_URL: data.reservationDocumentUrl || null,
  NEED_FLG: data.needFlg === false ? "0" : "1",
  MEMBERS: buildScheduleMembers(data.shioriId, data.members || []),
  __guest: true,
});

export const createGuestSchedule = (data) => {
  const lineNon = `guest-schedule-${guestState.scheduleSeq++}`;
  const schedule = toGuestSchedule(data, lineNon);
  guestState.schedulesByShioriId[data.shioriId] = [
    ...(guestState.schedulesByShioriId[data.shioriId] || []),
    schedule,
  ];
  saveGuestState();
  return schedule;
};

export const updateGuestSchedule = (shioriId, lineNon, data) => {
  const schedules = guestState.schedulesByShioriId[shioriId] || [];
  const index = schedules.findIndex((item) => item.LINE_NON === lineNon);
  if (index < 0) throw new Error("スケジュールが見つかりません");
  schedules[index] = toGuestSchedule({ ...data, shioriId }, lineNon);
  saveGuestState();
  return schedules[index];
};

export const deleteGuestSchedule = (shioriId, lineNon) => {
  guestState.schedulesByShioriId[shioriId] = (
    guestState.schedulesByShioriId[shioriId] || []
  ).filter((item) => item.LINE_NON !== lineNon);
  saveGuestState();
  return { success: true };
};

export const fetchGuestChecklists = (shioriId) =>
  guestState.checklistsByShioriId[shioriId] || [];

export const createGuestChecklist = (shioriId, itemType, data = {}) => {
  const checklistNo = `guest-checklist-${guestState.checklistSeq++}`;
  const item = {
    PAGE_NO: 1,
    CHECKLIST_NO: checklistNo,
    ITEM_TYPE: itemType,
    CATEGORY_NAME:
      data.categoryName || (itemType === "BELONGINGS" ? "持ち物" : "To Do"),
    ITEM_NAME:
      data.itemName ||
      (itemType === "BELONGINGS" ? "お試しの持ち物" : "お試しのTo Do"),
    MEMO_TEXT: data.memoText || null,
    DEADLINE: data.deadline || null,
    PRIORITY_LEVEL: data.priorityLevel || "NORMAL",
    DEADLINE_NOTIFY_FLG: data.deadlineNotify ? "1" : "0",
    COMPLETED_FLG: "0",
    MEMBERS: data.members || [],
  };
  guestState.checklistsByShioriId[shioriId] = [
    ...(guestState.checklistsByShioriId[shioriId] || []),
    item,
  ];
  saveGuestState();
  return item;
};

export const updateGuestChecklist = (
  shioriId,
  pageNo,
  checklistNo,
  data = {},
) => {
  const items = guestState.checklistsByShioriId[shioriId] || [];
  const target = items.find(
    (item) =>
      String(item.PAGE_NO) === String(pageNo) &&
      String(item.CHECKLIST_NO) === String(checklistNo),
  );
  if (!target) throw new Error("チェックリストが見つかりません");

  const selectedCodes = data.assignedMemberCodes || [];
  Object.assign(target, {
    ITEM_NAME: data.itemName ?? target.ITEM_NAME,
    MEMO_TEXT: data.memoText ?? target.MEMO_TEXT,
    CATEGORY_NAME: data.categoryName ?? target.CATEGORY_NAME,
    DEADLINE: data.deadline ?? target.DEADLINE,
    PRIORITY_LEVEL: data.priorityLevel ?? target.PRIORITY_LEVEL,
    DEADLINE_NOTIFY_FLG:
      data.deadlineNotifyFlg ?? target.DEADLINE_NOTIFY_FLG,
    COMPLETED_FLG: data.completedFlg ?? target.COMPLETED_FLG,
    ASSIGNED_MEMBER_CODE:
      data.assignedMemberCode ?? selectedCodes[0] ?? target.ASSIGNED_MEMBER_CODE,
    PRIVATE_FLG: data.privateFlg ?? target.PRIVATE_FLG,
    MEMBERS:
      selectedCodes.length > 0
        ? selectedCodes.map((memberCode) => ({
            ASSIGNEE_USER_CODE: memberCode,
            MEMBER_CODE: memberCode,
          }))
        : target.MEMBERS,
  });
  saveGuestState();
  return target;
};

export const deleteGuestChecklist = (shioriId, pageNo, checklistNo) => {
  guestState.checklistsByShioriId[shioriId] = (
    guestState.checklistsByShioriId[shioriId] || []
  ).filter(
    (item) =>
      String(item.PAGE_NO) !== String(pageNo) ||
      String(item.CHECKLIST_NO) !== String(checklistNo),
  );
  saveGuestState();
  return { success: true };
};

export const fetchGuestWishlist = (shioriId) =>
  (guestState.wishlistsByShioriId[shioriId] || []).map((item) => ({
    ...item,
    TAGS: Array.isArray(item.TAGS)
      ? item.TAGS.reduce((tags, tag) => {
          if (tags.length >= 10 || typeof tag !== "string") return tags;
          const normalized = tag.trim();
          if (!normalized || normalized.length > 20 || tags.includes(normalized)) return tags;
          tags.push(normalized);
          return tags;
        }, [])
      : [],
  }));

export const createGuestWishlist = (data = {}) => {
  const shioriId = data.shioriId;
  const wishNo = `guest-wish-${guestState.wishlistSeq++}`;
  const item = {
    SHIORI_NOV: shioriId,
    WISH_NO: wishNo,
    PLACE_NMV: data.placeNmv || "行きたいところ",
    CATEGORY_NMV: data.categoryNmv || null,
    ADDRESS_NMV: data.addressNmv || null,
    LATITUDE: data.latitude ?? null,
    LONGITUDE: data.longitude ?? null,
    URL_NMV: data.urlNmv || null,
    MEMO_TXV: data.memoTxv || null,
    AMOUNT: data.amount ?? null,
    CURRENCY: "JPY",
    STATUS_CDV: data.statusCdv || "検討中",
    VOTE_COUNT: 0,
    MY_VOTE: false,
    DELETE_FLG: "0",
    TAGS: Array.isArray(data.tags) ? [...data.tags] : [],
    __guest: true,
  };
  guestState.wishlistsByShioriId[shioriId] = [
    ...(guestState.wishlistsByShioriId[shioriId] || []),
    item,
  ];
  saveGuestState();
  return item;
};

export const updateGuestWishlist = (shioriId, wishNo, data = {}) => {
  const items = guestState.wishlistsByShioriId[shioriId] || [];
  const target = items.find((item) => item.WISH_NO === wishNo);
  if (!target) throw new Error("行きたいところが見つかりません");

  Object.assign(target, {
    PLACE_NMV: data.placeNmv ?? target.PLACE_NMV,
    CATEGORY_NMV: data.categoryNmv ?? target.CATEGORY_NMV,
    ADDRESS_NMV: data.addressNmv ?? target.ADDRESS_NMV,
    LATITUDE: data.latitude ?? target.LATITUDE,
    LONGITUDE: data.longitude ?? target.LONGITUDE,
    URL_NMV: data.urlNmv ?? target.URL_NMV,
    MEMO_TXV: data.memoTxv ?? target.MEMO_TXV,
    AMOUNT: data.amount ?? target.AMOUNT,
    CURRENCY: data.currency ?? target.CURRENCY,
    STATUS_CDV: data.statusCdv ?? target.STATUS_CDV,
    TAGS: Array.isArray(data.tags) ? [...data.tags] : target.TAGS || [],
  });
  saveGuestState();
  return target;
};

export const deleteGuestWishlist = (shioriId, wishNo) => {
  guestState.wishlistsByShioriId[shioriId] = (
    guestState.wishlistsByShioriId[shioriId] || []
  ).filter((item) => item.WISH_NO !== wishNo);
  saveGuestState();
  return { success: true };
};

export const toggleGuestWishlistVote = (shioriId, wishNo) => {
  const item = (guestState.wishlistsByShioriId[shioriId] || []).find(
    (candidate) => candidate.WISH_NO === wishNo,
  );
  if (!item) throw new Error("行きたいところが見つかりません");

  item.MY_VOTE = !item.MY_VOTE;
  item.VOTE_COUNT = Math.max(
    0,
    Number(item.VOTE_COUNT || 0) + (item.MY_VOTE ? 1 : -1),
  );
  saveGuestState();
  return { voted: item.MY_VOTE };
};

export const fetchGuestWarikans = (shioriId) =>
  guestState.warikansByShioriId[shioriId] || [];

export const fetchGuestWarikanMembers = (shioriId) =>
  guestState.warikanMembersByShioriId[shioriId] || [];

export const fetchGuestExchangeRates = (shioriId) => [
  {
    SHIORI_NOV: shioriId,
    CURRENCY: "JPY",
    RATE_TO_JPY: 1,
    RATE_DATE: null,
    SOURCE: "system",
    UPDATE_YMD: new Date().toISOString().slice(0, 10),
  },
  ...(guestState.exchangeRatesByShioriId[shioriId] || []),
];

export const upsertGuestExchangeRate = (shioriId, rate = {}) => {
  const currency = String(rate.currency || rate.CURRENCY || "JPY")
    .trim()
    .toUpperCase();
  const rateToJpy = Number(rate.rateToJpy ?? rate.RATE_TO_JPY);
  if (currency === "JPY") {
    return fetchGuestExchangeRates(shioriId)[0];
  }
  if (
    !/^[A-Z]{3}$/.test(currency) ||
    !Number.isFinite(rateToJpy) ||
    rateToJpy <= 0
  ) {
    throw new Error("為替レートを入力してください");
  }
  const row = {
    SHIORI_NOV: shioriId,
    CURRENCY: currency,
    RATE_TO_JPY: rateToJpy,
    RATE_DATE: rate.rateDate || rate.RATE_DATE || null,
    SOURCE: rate.source || rate.SOURCE || "manual",
    UPDATE_YMD: new Date().toISOString().slice(0, 10),
  };
  const rows = guestState.exchangeRatesByShioriId[shioriId] || [];
  const index = rows.findIndex((item) => item.CURRENCY === currency);
  if (index >= 0) {
    rows[index] = row;
  } else {
    rows.push(row);
  }
  guestState.exchangeRatesByShioriId[shioriId] = rows;
  saveGuestState();
  return row;
};

export const createGuestWarikan = (shioriId, data = {}) => {
  const lineNon = `guest-warikan-${guestState.warikanSeq++}`;
  const members = data.memberCodes || fetchGuestMembers(shioriId).map((m) => m.MEMBER_CODE);
  const warikan = {
    LINE_NON: lineNon,
    SUBJECT: data.subject || "お試しの割り勘",
    AMOUNT: Number(data.amount || 1000),
    CURRENCY: data.currency || "JPY",
    PAYER_USER_CODE: data.payerCode || members[0] || null,
    EXPENSE_SETTLED_FLG: "0",
    MEMO_TEXT: data.memoText || null,
  };
  guestState.warikansByShioriId[shioriId] = [
    ...(guestState.warikansByShioriId[shioriId] || []),
    warikan,
  ];
  guestState.warikanMembersByShioriId[shioriId] = [
    ...(guestState.warikanMembersByShioriId[shioriId] || []),
    ...members.map((memberCode) => ({
      LINE_NON: lineNon,
      BENEFICIARY_USER_CODE: memberCode,
    })),
  ];
  saveGuestState();
  return warikan;
};
